import { useId, useRef, useState } from 'react';
import {
  CLIP_SECONDS,
  MODELS,
  TIERS,
  costOf,
  creditsToEuro,
  quoteTier,
} from '../lib/pricing';
import {
  formatEuro,
  formatEuroPrecise,
  formatNumber,
  formatPercent,
  relativeTime,
} from '../lib/format';
import { actions, useAppState } from '../state/store';
import { IconArrow, IconDownload, IconWallet } from '../components/Icons';
import { toast } from '../components/Toaster';

export function CreditsView() {
  const { credits, ledger, history, settings } = useAppState();
  const [index, setIndex] = useState(2);
  const sliderId = useId();
  const importInput = useRef<HTMLInputElement | null>(null);

  const exportData = () => {
    const json = actions.exportState();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hunyadi-backup-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast('Backup downloaded.', 'good');
  };

  const importData = async (file: File) => {
    try {
      const text = await file.text();
      const { addedHistory, addedLedger } = actions.importAndMergeState(text);
      if (addedHistory === 0 && addedLedger === 0) {
        toast('Nothing new in that file — already merged.', 'good');
      } else {
        toast(
          `Merged: +${addedHistory} clip${addedHistory === 1 ? '' : 's'}, +${addedLedger} ledger entr${addedLedger === 1 ? 'y' : 'ies'}.`,
          'good',
        );
      }
    } catch {
      toast('Could not read that file — is it a Hunyadi backup?', 'bad');
    }
  };

  const quote = quoteTier(TIERS[index]);
  // "≈ N clips" uses whatever model is currently selected in the studio, so the
  // number on this screen matches what the next Generate will actually cost.
  const currentCost = costOf(settings);
  const clips = Math.floor(quote.totalCredits / currentCost);
  const fill = (index / (TIERS.length - 1)) * 100;

  const buy = () => {
    // NOTE: purely local. No payment provider is contacted — the credits are
    // written straight into localStorage via the store.
    actions.purchase(quote.tier.euro, quote.totalCredits, quote.bonusCredits);
    toast(
      `${formatNumber(quote.totalCredits)} credits added (${formatEuro(quote.tier.euro)} pack).`,
      'good',
    );
  };

  return (
    <>
      <div className="wrap view-head">
        <span className="eyebrow">Credits</span>
        <h1 className="view-title">
          Pay for what you render.
          <br />
          Bigger packs, <b>bigger bonus</b>.
        </h1>
        <p className="view-lead">
          No subscription, no monthly fee — credits are spent per second of video and never
          expire. Every model carries its own rate, so you always pay the true cost of the
          engine you picked. Balances are stored locally on this machine; no real payment is
          taken.
        </p>
      </div>

      <div className="wrap credits-grid">
        {/* ── Slider + live quote ───────────────────────────────────────── */}
        <section className="panel">
          <div className="panel-head">
            <h2 className="panel-title">Pack calculator</h2>
            <span className="field-hint">{quote.tier.label}</span>
          </div>

          <div className="quote-hero">
            <div className="quote-cell">
              <span className="quote-num euro mono">{formatEuro(quote.tier.euro)}</span>
              <span className="quote-label">you pay</span>
            </div>
            <div className="quote-arrow">
              <IconArrow size={20} />
              <span className={`bonus-flag ${quote.tier.bonus === 0 ? 'is-zero' : ''}`}>
                {quote.tier.bonus === 0 ? 'no bonus' : `+${formatPercent(quote.tier.bonus)} bonus`}
              </span>
            </div>
            <div className="quote-cell right">
              <span className="quote-num credits mono">{formatNumber(quote.totalCredits)}</span>
              <span className="quote-label">credits</span>
            </div>
          </div>

          <div className="slider-block">
            <label className="sr-only" htmlFor={sliderId}>
              Pack size
            </label>
            <input
              id={sliderId}
              type="range"
              min={0}
              max={TIERS.length - 1}
              step={1}
              value={index}
              style={{ ['--fill' as string]: `${fill}%` }}
              aria-valuetext={`${quote.tier.euro} euro — ${quote.totalCredits} credits`}
              onChange={(e) => setIndex(Number(e.target.value))}
            />
            <div className="tick-row">
              {TIERS.map((t, i) => (
                <button
                  key={t.euro}
                  type="button"
                  className={`tick ${i === index ? 'is-active' : ''}`}
                  onClick={() => setIndex(i)}
                  aria-label={`${t.euro} euro pack`}
                >
                  €{t.euro}
                </button>
              ))}
            </div>
          </div>

          <div className="estimate-row">
            <div className="estimate-cell">
              <span className="e-value mono" style={{ color: 'var(--accent)' }}>
                ≈ {formatNumber(clips)}
              </span>
              <span className="e-label">clips at current settings</span>
            </div>
            <div className="estimate-cell">
              <span className="e-value mono">
                {quote.bonusCredits > 0 ? `+${formatNumber(quote.bonusCredits)}` : '—'}
              </span>
              <span className="e-label">bonus credits</span>
            </div>
            <div className="estimate-cell">
              <span className="e-value mono">€{quote.pricePerCredit.toFixed(4)}</span>
              <span className="e-label">
                per credit
                {quote.savingsVsBase > 0.001 && (
                  <span style={{ color: 'var(--accent)' }}>
                    {' '}
                    · −{formatPercent(quote.savingsVsBase)}
                  </span>
                )}
              </span>
            </div>
          </div>

          <div
            style={{ padding: 'var(--pad-panel)', display: 'flex', flexDirection: 'column', gap: 10 }}
          >
            <button type="button" className="btn btn-primary btn-lg btn-block" onClick={buy}>
              <IconWallet size={17} />
              Add {formatNumber(quote.totalCredits)} credits — {formatEuro(quote.tier.euro)}
            </button>
            <p className="hint-line">
              Simulated purchase · no payment provider · balance stored on this machine
              <br />
              Balance after: {formatNumber(credits + quote.totalCredits)} credits
            </p>
          </div>
        </section>

        {/* ── Tiers, rate card, ledger ──────────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-lg)' }}>
          <section className="panel">
            <div className="panel-head">
              <h2 className="panel-title">Packs</h2>
              <span className="field-hint">bonus 0 → 30%</span>
            </div>
            <div style={{ padding: 12 }}>
              <div className="tier-list">
                {TIERS.map((t, i) => {
                  const q = quoteTier(t);
                  return (
                    <button
                      key={t.euro}
                      type="button"
                      className="tier-row"
                      aria-pressed={i === index}
                      onClick={() => setIndex(i)}
                    >
                      <span className="tier-euro">€{t.euro}</span>
                      <span className="tier-info">
                        <span className="tier-name">{t.label}</span>
                        <span className="tier-detail">
                          {formatNumber(q.totalCredits)} credits · €
                          {q.pricePerCredit.toFixed(4)}/cr
                        </span>
                      </span>
                      <span className={`bonus-flag ${t.bonus === 0 ? 'is-zero' : ''}`}>
                        {t.bonus === 0 ? '—' : `+${formatPercent(t.bonus)}`}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Rate card — makes the pay-as-you-use structure explicit and shows
              what plugging in more engines will cost. */}
          <section className="panel">
            <div className="panel-head">
              <h2 className="panel-title">Rate card</h2>
              <span className="field-hint">per {CLIP_SECONDS}s clip · 720p</span>
            </div>
            <div style={{ padding: 12 }}>
              <div className="tier-list">
                {MODELS.map((m) => {
                  const perClip = Math.ceil(
                    (m.creditsPerSecond + m.audioCreditsPerSecond) * CLIP_SECONDS,
                  );
                  return (
                    <div key={m.id} className="tier-row" aria-disabled={!m.available}>
                      <span
                        className="tier-euro"
                        style={{ color: m.available ? 'var(--accent)' : 'var(--ink-faint)' }}
                      >
                        {perClip}
                        <span style={{ fontSize: 10, opacity: 0.7 }}> cr</span>
                      </span>
                      <span className="tier-info">
                        <span className="tier-name">
                          {m.label}
                          {!m.available && (
                            <span className="soon-flag">soon</span>
                          )}
                        </span>
                        <span className="tier-detail">
                          {m.vendor} · ≈{formatEuroPrecise(creditsToEuro(perClip))}
                          {m.audioCreditsPerSecond > 0 ? ' with audio' : ''}
                        </span>
                      </span>
                    </div>
                  );
                })}
              </div>
              <p className="hint-line" style={{ marginTop: 12, textAlign: 'left' }}>
                Rates scale with resolution and clip length. Local generation runs on this
                machine's GPU, which is why it costs a fraction of the hosted APIs.
              </p>
            </div>
          </section>

          <section className="panel">
            <div className="panel-head">
              <h2 className="panel-title">Activity</h2>
              <span className="field-hint mono">{ledger.length} entries</span>
            </div>
            <div className="panel-body">
              {ledger.length === 0 ? (
                <p className="empty-sub">Nothing yet.</p>
              ) : (
                <div className="ledger">
                  {ledger.slice(0, 12).map((entry) => (
                    <div key={entry.id} className="ledger-row">
                      <span
                        style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}
                      >
                        <span className="ledger-note">{entry.note}</span>
                        <span className="ledger-time">{relativeTime(entry.createdAt)}</span>
                      </span>
                      <span className={`ledger-amount ${entry.credits >= 0 ? 'plus' : 'minus'}`}>
                        {entry.credits >= 0 ? '+' : ''}
                        {formatNumber(entry.credits)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Export/Import — the only way to reconcile data that split across
              two different addresses (e.g. localhost:5173 vs :4173), since
              browsers keep those as two entirely separate storage buckets
              with no way for the app itself to merge them automatically. */}
          <section className="panel">
            <div className="panel-head">
              <h2 className="panel-title">Backup &amp; sync</h2>
              <span className="field-hint mono">{history.length} clips</span>
            </div>
            <div className="panel-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <p className="hint-line" style={{ textAlign: 'left' }}>
                Credits and history live in this browser tab's local storage, tied to the exact
                address you're on. If you ever end up with different numbers in different
                windows, it's almost always because they're on different addresses (e.g.{' '}
                <code className="mono">localhost:5173</code> vs{' '}
                <code className="mono">localhost:4173</code>) — export from one, import into the
                other, and nothing is lost.
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="btn btn-sm" style={{ flex: 1 }} onClick={exportData}>
                  <IconDownload size={14} /> Export backup
                </button>
                <button
                  type="button"
                  className="btn btn-sm"
                  style={{ flex: 1 }}
                  onClick={() => importInput.current?.click()}
                >
                  Import &amp; merge
                </button>
              </div>
              <input
                ref={importInput}
                type="file"
                accept="application/json"
                className="sr-only"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void importData(file);
                  e.target.value = '';
                }}
              />
            </div>
          </section>
        </div>
      </div>
    </>
  );
}
